import { GamePhase, type GameState, type NPCData, type TownData, type NearbyHeroData } from '@/game/types';

export type HeroAnimationFacing = 'south' | 'south-east' | 'east' | 'north-east' | 'north' | 'north-west' | 'west' | 'south-west';
export interface HeroAnimationView {
  readonly clip: 'idle' | 'idle-breathing' | 'idle-catwalk' | 'idle-bored' | 'idle-stretch' | 'walk' | 'combat-idle' | 'attack' | 'block' | 'dodge';
  readonly facing: HeroAnimationFacing;
  readonly frame: number;
  readonly progress?: number;
}

/** Deep read-only data only: no engine, camera instance, React or Pixi objects. */
export type ReadonlyData<T> = T extends readonly (infer Item)[]
  ? readonly ReadonlyData<Item>[]
  : T extends object ? { readonly [Key in keyof T]: ReadonlyData<T[Key]> } : T;
export interface CameraView { readonly finalX: number; readonly finalY: number; readonly zoom?: number; }
export interface SpeechBubbleView {
  readonly wx: number; readonly wy: number; readonly text: string;
  readonly startMs: number; readonly ownPlayerMessage: boolean;
}
export type NearbyHeroPresentation = NearbyHeroData & { readonly motion?: HeroAnimationView };
export interface RenderSnapshot {
  readonly nowMs: number;
  readonly serverTimeMs?: number;
  readonly viewport: Readonly<{ width: number; height: number }>;
  readonly camera: CameraView;
  readonly hero: Readonly<{ id: number; x: number; y: number; animation: 'idle' | 'walk' | 'fight'; modelVariant: number; motion?: HeroAnimationView }> | null;
  readonly restCamp: Readonly<{ x: number; y: number }> | null;
  readonly enemy: Readonly<{ x: number; y: number; slug: string }> | null;
  readonly towns: readonly ReadonlyData<TownData>[];
  readonly npcs: readonly ReadonlyData<NPCData>[];
  readonly nearbyHeroes: readonly ReadonlyData<NearbyHeroPresentation>[];
  readonly bubbles: readonly SpeechBubbleView[];
}

export interface FramePresentation {
  nowMs: number; viewport: RenderSnapshot['viewport']; camera: CameraView;
  heroPosition: Readonly<{ x: number; y: number }>;
  heroMoving?: boolean;
  heroMotion?: HeroAnimationView;
  towns: RenderSnapshot['towns']; npcs: RenderSnapshot['npcs'];
  nearbyHeroes: RenderSnapshot['nearbyHeroes']; bubbles: RenderSnapshot['bubbles'];
}

/** Select presentation from reported server state. It never decides combat outcomes or awards rewards. */
export function createRenderSnapshot(state: Readonly<GameState>, frame: FramePresentation): RenderSnapshot {
  const hero = state.hero;
  const meet = hero?.excursionKind === 'hero_meet' && hero.excursionPhase?.toLowerCase() === 'meet';
  const animation = state.phase === GamePhase.Walking ? (meet ? 'idle' : 'walk')
    : state.phase === GamePhase.Fighting ? 'fight' : 'idle';
  const restKind = hero?.restKind?.toLowerCase();
  // The server can arrive before the buffered display path. Do not attach the camp
  // (or its resting pose) to that moving tail; the reported stop owns its anchor.
  const showCamp = hero && !frame.heroMoving && state.phase === GamePhase.Resting && hero.serverActivityState?.toLowerCase() === 'resting'
    && hero.excursionPhase?.toLowerCase() === 'wild' && (restKind === 'roadside' || restKind === 'adventure_inline')
    && Math.hypot(frame.heroPosition.x - hero.position.x, frame.heroPosition.y - hero.position.y) <= 1e-6;
  const enemy = state.enemy && (state.phase === GamePhase.Fighting || state.phase === GamePhase.Dead)
    ? { x: state.enemy.position.x, y: state.enemy.position.y, slug: state.enemy.enemySlug } : null;
  return {
    nowMs: frame.nowMs, serverTimeMs: state.serverTimeMs, viewport: frame.viewport,
    camera: { finalX: frame.camera.finalX, finalY: frame.camera.finalY, zoom: frame.camera.zoom ?? 1 },
    hero: hero ? { id: hero.id, ...frame.heroPosition, animation, modelVariant: hero.modelVariant ?? 0, ...(frame.heroMotion ? { motion: { ...frame.heroMotion } } : {}) } : null,
    restCamp: showCamp ? { ...hero.position } : null, enemy,
    towns: frame.towns, npcs: frame.npcs, nearbyHeroes: frame.nearbyHeroes, bubbles: frame.bubbles,
  };
}
